import { API_BASE_URL, apiClient, getAuthHeaders, type StepUpRequestConfig } from "../api/client";
import { setLiveStatus, type LiveTransport } from "./useLiveUpdates.state";

// Release 1.5.0 — this tab's one push connection to /api/v1/hubs/live (SignalR). It is opened with a one-time ticket
// (POST /hubs/live/ticket with the normal sign-in) so no sign-in token ever appears in a URL. WebSockets first; if they
// are blocked anywhere on the way (a proxy, an old browser), long polling with the normal sign-in headers. Server-Sent
// Events are never used (Azure Front Door does not carry them). When the connection drops, it is re-opened with growing,
// jittered waits; after two minutes of failures the tab settles on the 30-second check and tries again every minute.

export interface LiveSnapshot {
  nonce: string;
  areas: Record<string, number>;
}

export interface LiveChanged {
  nonce: string;
  changes: Array<{ area: string; version: number }>;
}

/** The slice of a SignalR HubConnection the manager uses (a fake one in tests). */
export interface LiveHubConnection {
  start(): Promise<void>;
  stop(): Promise<void>;
  on(method: "snapshot" | "changed", handler: (message: unknown) => void): void;
  onclose(handler: (error?: Error) => void): void;
}

export type LiveConnectRequest =
  | { transport: "webSockets"; url: string }
  | { transport: "longPolling"; url: string; headers: Record<string, string> };

export type LiveConnectionFactory = (request: LiveConnectRequest) => Promise<LiveHubConnection>;

export interface LiveUpdatesHandlers {
  onSnapshot(message: LiveSnapshot): void;
  onChanged(message: LiveChanged): void;
}

/** Waits between reconnect attempts after a drop (before ±20 % jitter). */
export const RECONNECT_DELAYS_MS = [0, 1_000, 2_000, 5_000, 10_000, 20_000, 30_000] as const;
/** After this long without a connection the tab settles on the 30-second check. */
export const GIVE_UP_AFTER_MS = 120_000;
/** While settled on the 30-second check, how often a new connection is tried. */
export const RETRY_WHILE_POLLING_MS = 60_000;
/** After the server refused (not signed in, not allowed, switched off, step-up needed), how long before asking again. */
export const RETRY_AFTER_REFUSAL_MS = 5 * 60_000;

/** The default factory: @microsoft/signalr, loaded only when a tab actually connects (its own small chunk). */
export const signalRFactory: LiveConnectionFactory = async (request) => {
  const signalR = await import("@microsoft/signalr");
  const builder = new signalR.HubConnectionBuilder().configureLogging(signalR.LogLevel.None);
  const connection =
    request.transport === "webSockets"
      ? builder.withUrl(request.url, { transport: signalR.HttpTransportType.WebSockets, skipNegotiation: true }).build()
      : builder.withUrl(request.url, { transport: signalR.HttpTransportType.LongPolling, headers: request.headers }).build();
  return {
    start: () => connection.start(),
    stop: () => connection.stop(),
    on: (method, handler) => connection.on(method, handler),
    onclose: (handler) => connection.onclose(handler)
  };
};

class RefusedError extends Error {}

function jitter(ms: number, random: () => number): number {
  return Math.round(ms * (0.8 + 0.4 * random()));
}

function absolute(path: string): string {
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.href).toString();
}

export interface LiveUpdatesManagerOptions {
  factory?: LiveConnectionFactory;
  requestTicket?: () => Promise<string>;
  authHeaders?: () => Promise<Record<string, string>>;
  now?: () => number;
  random?: () => number;
}

/** Asks the API for a one-time connection ticket. Never opens the "confirm it's you" prompt. */
export async function requestLiveTicket(): Promise<string> {
  const config: StepUpRequestConfig = { skipStepUpPrompt: true };
  const res = await apiClient.post<{ ticket: string }>("/hubs/live/ticket", null, config);
  if (!res.data?.ticket) throw new RefusedError("No ticket.");
  return res.data.ticket;
}

function isRefusal(error: unknown): boolean {
  if (error instanceof RefusedError) return true;
  const e = error as { status?: number; statusCode?: number } | null;
  const status = e?.status ?? e?.statusCode;
  return status === 401 || status === 403 || status === 404 || status === 428;
}

/**
 * One push connection with its own reconnect loop (no SignalR automatic reconnect: every attempt needs a fresh ticket
 * and fresh sign-in headers).
 */
export class LiveUpdatesManager {
  private readonly factory: LiveConnectionFactory;
  private readonly requestTicket: () => Promise<string>;
  private readonly authHeaders: () => Promise<Record<string, string>>;
  private readonly now: () => number;
  private readonly random: () => number;
  private connection: LiveHubConnection | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private connecting = false;
  private everLive = false;
  private failures = 0;
  private failingSince: number | null = null;
  private generation = 0;

  constructor(
    private readonly handlers: LiveUpdatesHandlers,
    options: LiveUpdatesManagerOptions = {}
  ) {
    this.factory = options.factory ?? signalRFactory;
    this.requestTicket = options.requestTicket ?? requestLiveTicket;
    this.authHeaders = options.authHeaders ?? getAuthHeaders;
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.everLive = false;
    this.failures = 0;
    this.failingSince = null;
    setLiveStatus("polling");
    this.schedule(0);
  }

  async stop(): Promise<void> {
    this.running = false;
    this.generation += 1;
    this.clearTimer();
    const connection = this.connection;
    this.connection = null;
    setLiveStatus("off");
    if (connection) {
      try {
        await connection.stop();
      } catch {
        // Already closed.
      }
    }
  }

  /** Try now (the browser came back online, or the tab became visible) unless connected or already trying. */
  retryNow(): void {
    if (!this.running || this.connection || this.connecting) return;
    this.schedule(0);
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(delayMs: number): void {
    this.clearTimer();
    const generation = this.generation;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.running && generation === this.generation) void this.connect(generation);
    }, delayMs);
  }

  private async connect(generation: number): Promise<void> {
    if (this.connecting || this.connection) return;
    this.connecting = true;
    try {
      const ticket = await this.requestTicket();
      if (!this.isCurrent(generation)) return;
      const connection = await this.open(ticket, generation);
      if (!connection) return;
    } catch (error) {
      if (!this.isCurrent(generation)) return;
      this.failed(isRefusal(error));
    } finally {
      this.connecting = false;
    }
  }

  private isCurrent(generation: number): boolean {
    return this.running && generation === this.generation;
  }

  private async open(ticket: string, generation: number): Promise<LiveHubConnection | null> {
    const base = absolute(`${API_BASE_URL}/hubs/live`);
    const attempts: Array<() => Promise<{ connection: LiveHubConnection; transport: LiveTransport }>> = [
      async () => ({
        connection: await this.factory({ transport: "webSockets", url: `${base}?ticket=${encodeURIComponent(ticket)}` }),
        transport: "webSockets"
      }),
      async () => ({
        connection: await this.factory({ transport: "longPolling", url: base, headers: await this.authHeaders() }),
        transport: "longPolling"
      })
    ];

    let lastError: unknown = null;
    for (const attempt of attempts) {
      let candidate: LiveHubConnection | null = null;
      try {
        const { connection, transport } = await attempt();
        candidate = connection;
        connection.on("snapshot", (m) => this.isCurrent(generation) && this.handlers.onSnapshot(m as LiveSnapshot));
        connection.on("changed", (m) => this.isCurrent(generation) && this.handlers.onChanged(m as LiveChanged));
        connection.onclose(() => this.closed(connection, generation));
        await connection.start();
        if (!this.isCurrent(generation)) {
          void connection.stop().catch(() => undefined);
          return null;
        }
        this.connection = connection;
        this.everLive = true;
        this.failures = 0;
        this.failingSince = null;
        setLiveStatus("live", transport);
        return connection;
      } catch (error) {
        lastError = error;
        if (candidate) void candidate.stop().catch(() => undefined);
        if (isRefusal(error)) break;
      }
    }
    throw lastError ?? new Error("The live-updates connection could not be opened.");
  }

  private closed(connection: LiveHubConnection, generation: number): void {
    if (this.connection !== connection || !this.isCurrent(generation)) return;
    this.connection = null;
    this.failingSince = this.now();
    this.failures = 0;
    setLiveStatus("reconnecting");
    this.schedule(jitter(RECONNECT_DELAYS_MS[0], this.random));
  }

  private failed(refused: boolean): void {
    if (refused) {
      setLiveStatus("polling");
      this.schedule(jitter(RETRY_AFTER_REFUSAL_MS, this.random));
      return;
    }
    if (this.failingSince === null) this.failingSince = this.now();
    this.failures += 1;
    const failingFor = this.now() - this.failingSince;
    if (!this.everLive || failingFor >= GIVE_UP_AFTER_MS) {
      setLiveStatus("polling");
      const delay = this.everLive || this.failures > 1 ? RETRY_WHILE_POLLING_MS : RECONNECT_DELAYS_MS[1];
      this.schedule(jitter(delay, this.random));
      return;
    }
    setLiveStatus("reconnecting");
    const index = Math.min(this.failures, RECONNECT_DELAYS_MS.length - 1);
    this.schedule(jitter(RECONNECT_DELAYS_MS[index] ?? RETRY_WHILE_POLLING_MS, this.random));
  }
}
